import React from "react";
import {
  type GridColDef,
  type GridColumnGroupingModel,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { type SxProps, type Theme } from "@mui/material";
import {
  ALL_MATTERS_FIELDS,
  CHECKBOX_FIELDS,
  isAllMattersField,
} from "../utils/checkboxFields";
import { type BulkPropertyRow } from "../types";
import { BulkCheckboxCell } from "./BulkCheckboxCell";
import { BulkRowSourceCell } from "./BulkRowSourceCell";

/**
 * 不可のチェックボックスは既定の非活性色だと可のものと見分けにくいため、さらに薄くする
 */
export const BULK_MARK_SX: SxProps<Theme> = {
  "& .MuiCheckbox-root.Mui-disabled": {
    opacity: 0.45,
  },
};

/** 行のチェック済み書類が送信時にどう振り分けられるか（取得済／リユース／新規取得）を出す列 */
export const ROW_SOURCE_FIELD = "acquireSource";

const checkboxColumn = ({
  field,
  headerName,
  width,
}: (typeof CHECKBOX_FIELDS)[number]): GridColDef => ({
  field,
  headerName,
  width,
  headerAlign: "center",
  align: "center",
  sortable: false,
  renderCell: (params: GridRenderCellParams<unknown, BulkPropertyRow>) => (
    <BulkCheckboxCell field={field} headerName={headerName} row={params.row} />
  ),
});

// 「地積測量図: リユース 2026-03-27」が欠けない幅
const ROW_SOURCE_COLUMN: GridColDef = {
  field: ROW_SOURCE_FIELD,
  headerName: "取得方法",
  width: 240,
  sortable: false,
  renderCell: (params: GridRenderCellParams<unknown, BulkPropertyRow>) => (
    <BulkRowSourceCell row={params.row} />
  ),
};

// 列定義はチェック状態・取得条件のいずれにも依存しない（セル側が購読する）ため固定で持つ。
// 所在系の列とチェック列の間に行単位の取得方法列を置く
export const BULK_COLUMNS: GridColDef[] = [
  {
    field: "companyName",
    headerName: "会社名",
    width: 260,
    sortable: false,
  },
  { field: "id", headerName: "No", width: 90, sortable: false },
  {
    field: "realEstateType",
    headerName: "種別",
    width: 90,
    sortable: false,
  },
  {
    field: "prefectureName",
    headerName: "都道府県",
    width: 90,
    sortable: false,
  },
  {
    field: "location",
    headerName: "所在",
    width: 240,
    sortable: false,
  },
  {
    field: "chibanOrKaoku",
    headerName: "地番または家屋番号",
    width: 150,
    sortable: false,
    // 未取得物件は実値がレスポンスに含まれない（null）ため伏せ字にする
    valueFormatter: ({ value }) => (value == null ? "＊＊＊" : String(value)),
  },
  ROW_SOURCE_COLUMN,
  ...CHECKBOX_FIELDS.filter(({ field }) => isAllMattersField(field)).map(
    checkboxColumn
  ),
  ...CHECKBOX_FIELDS.filter(({ field }) => !isAllMattersField(field)).map(
    checkboxColumn
  ),
];

// 全部事項のカラムを結合する設定（既存の一括取得画面 FeedGrid と同じ構成）
export const BULK_COLUMN_GROUPING_MODEL: GridColumnGroupingModel = [
  {
    groupId: "全部事項",
    description: "",
    children: ALL_MATTERS_FIELDS.map((field) => ({ field })),
  },
];
